import type {
  ReactNode,
  ComponentType,
  SyntheticEvent,
  KeyboardEvent as SyntheticKeyboardEvent,
} from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

import { useMaybeViewer } from 'ms-helpers/Viewer/Renderer';
import {
  // ACCORDION_ANIMATION_DURATION,
  AccordionItem,
} from 'ms-pages/Teacher/components/Accordion';
import { fontFamily, fontSize, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import { useColorTheme } from 'ms-styles/themes';
import { FOCUSED_ACCESSIBILITY_MODE_BORDER_WIDTH } from 'ms-utils/accessibility';
import { styled, tappable } from 'ms-utils/emotion';
import { useRouter } from 'ms-utils/hooks/useRouter';
import keyDownMap, { type Action } from 'ms-utils/keyDownMap';

// It's useful to keep these ones the same
const BORDER_WIDTH = FOCUSED_ACCESSIBILITY_MODE_BORDER_WIDTH;

const ItemWrapper: ComponentType<{
  style: Record<string, unknown>;
  children: ReactNode;
}> = styled({
  default: {
    ...tappable,
    display: 'block',
    position: 'relative',
    textDecoration: 'none',
    borderLeftWidth: BORDER_WIDTH,
    borderLeftStyle: 'solid',
    paddingLeft: 2,
    color: 'inherit',
    ':focus': {
      outline: 'none',
    },
  },
});

const ITEM_VERTICAL_PADDING = BASE_UNIT;
const ITEM_LATERAL_PADDING = 4 * BASE_UNIT;

const Item: ComponentType<{
  accessibilityMode: boolean;
  active: boolean;
  children: ReactNode;
  to: LinkProps['to'];
  onClick?: ((event: SyntheticEvent<HTMLAnchorElement>) => void) | undefined;
  replace: boolean;
  onKeyDown: (event: SyntheticKeyboardEvent<HTMLAnchorElement>) => void;
  sunflower: boolean;
}> = styled(
  {
    default: {
      ...tappable,
      display: 'block',
      position: 'relative',
      textDecoration: 'none',
      fontFamily: fontFamily.body,
      color: colors.pickledBluewood,
      padding: `${ITEM_VERTICAL_PADDING}px ${ITEM_LATERAL_PADDING}px`,
      fontWeight: fontWeight.normal,
      fontSize: fontSize.small,
      border: `${BORDER_WIDTH}px solid transparent`,
      borderRadius: 4,
    },
    active: {
      color: colors.cloudBurst,
      fontWeight: fontWeight.semibold,
    },
    accessibilityMode: {
      ':focus': {
        outline: 'none',
        borderColor: colors.cloudBurst,
      },
    },
    sunflower: {
      paddingLeft: 0,
    },
  },
  Link as any, // ms-utils/emotion suckage
);

const ExtraContentOnActive = styled({
  default: {
    padding: `${ITEM_VERTICAL_PADDING}px ${ITEM_LATERAL_PADDING}px`,
  },
  sunflower: {
    padding: 0,
    width: '100%',
  },
});

type TabProps = {
  to: LinkProps['to'];
  active: boolean;
  children: ReactNode;
  renderOnActive?: (() => ReactNode) | undefined;
  onClick?: ((event: SyntheticEvent<HTMLAnchorElement>) => void) | undefined;
  onComponentMount?: (element: HTMLDivElement) => void;
  accessibilityMode: boolean;
};

export function SelectableListItem({
  to,
  onClick,
  active,
  children,
  accessibilityMode,
  onComponentMount,
  renderOnActive,
}: TabProps) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const wrapperEl = wrapperRef.current;
    if (onComponentMount != null && wrapperEl != null) {
      onComponentMount(wrapperEl);
    }
  }, [onComponentMount, wrapperRef]);

  const { primary: primaryColorName } = useColorTheme();
  const { history } = useRouter();

  const action = React.useCallback<Action<HTMLAnchorElement>>(
    e => {
      if (onClick != null) onClick(e);
      history.push(to);
    },
    [history, onClick, to],
  );

  const {
    featureFlags: { classicUi },
  } =
    // Must be useMAYBEViewer because it's consumed in free textbook too
    // where users are not logged in
    useMaybeViewer() || { featureFlags: { classicUi: true } };

  const borderColor = classicUi
    ? colors[active ? primaryColorName : 'iron']
    : 'transparent';

  return (
    <div ref={wrapperRef}>
      {(classicUi || (!classicUi && !active)) && (
        <ItemWrapper style={{ borderLeftColor: borderColor }}>
          <Item
            to={to}
            replace
            onClick={onClick}
            accessibilityMode={accessibilityMode}
            active={active}
            onKeyDown={keyDownMap<HTMLAnchorElement>({
              ENTER: [action, { preventDefault: true }],
              SPACE: [action, { preventDefault: true }],
            })}
            sunflower={!classicUi}
          >
            {children}
          </Item>
        </ItemWrapper>
      )}

      {renderOnActive != null && (
        <AccordionItem isOpen={active}>
          <ItemWrapper
            style={{ borderLeftColor: classicUi ? colors.iron : 'transparent' }}
          >
            <ExtraContentOnActive sunflower={!classicUi}>
              {renderOnActive()}
            </ExtraContentOnActive>
          </ItemWrapper>
        </AccordionItem>
      )}
    </div>
  );
}
